import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { selectProjectChannel } from '@/store/slices/project/selectors';
import GetEventRegistrantCounts from '@/connection/audiences/event-registrant-counts';
import { ProjectRegistrantCounts } from '@/types/registration.types';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';

export const useLoadEventRegistrantCounts = () => {
  const projectUuid = useProjectUuidParam();
  const channel = useAppSelector(selectProjectChannel);

  const fetching = useRef(false);
  const abortControllerRef = useRef(new AbortController());

  const [counts, setCounts] = useState<ProjectRegistrantCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const abort = useCallback(() => {
    fetching.current = false;
    const controller = abortControllerRef.current;
    if (!controller.signal.aborted) controller.abort();
  }, []);

  useEffect(() => {
    if (!channel || !projectUuid) {
      setCounts(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await GetEventRegistrantCounts({
          channel,
          projectUuid,
          signal: controller.signal,
        });

        if (!controller.signal.aborted) {
          setCounts(response);
          setLoading(false);
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error fetching event registrant counts:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch event registrant counts'));
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [channel, projectUuid]);

  return { counts, loading, error, abort };
};
